import {
  AudioOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  FileImageOutlined,
  HistoryOutlined,
  LinkOutlined,
  PaperClipOutlined,
  PlusOutlined,
  RobotOutlined,
  SendOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AssistantAction, AssistantConversation, AssistantMessage } from "@wms/domain";
import { Alert, App, Button, Card, Drawer, Empty, Grid, Input, List, Select, Space, Spin, Tag, Tooltip, Typography, Upload } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { assistant, requestId, uploadInvoice } from "../api/assistant";
import { ApiError, api, describeError } from "../api/client";
import type { Page } from "../api/client";
import { useAuthStore } from "../auth/store";
import { AssistantReview } from "../components/AssistantReview";
import { formatDateTime } from "../api/format";
import { PageHeader } from "../components/PageHeader";
import "./assistant.css";

interface Warehouse {
  id: string;
  code: string;
  name: string;
}

// ---- voice: the browser's own recogniser, the text is shown for correction and never sent by itself ---------------
interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

function recognitionClass(): (new () => Recognition) | null {
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const VOICE_LIMIT = 120;
const VOICE_ERRORS: Record<string, string> = {
  "not-allowed": "Нет доступа к микрофону — разрешите его в браузере или напишите задачу текстом",
  "service-not-allowed": "Распознавание речи недоступно в этом браузере — напишите задачу текстом",
  "no-speech": "Речь не услышана — повторите или напишите текстом",
  network: "Нет связи с сервисом распознавания речи — напишите задачу текстом",
  "audio-capture": "Микрофон не найден — напишите задачу текстом",
};

function useVoice(onText: (text: string) => void) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<Recognition | null>(null);
  const cancelled = useRef(false);
  const supported = !!recognitionClass();
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recording]);
  useEffect(() => {
    if (recording && seconds >= VOICE_LIMIT) ref.current?.stop();
  }, [recording, seconds]);
  useEffect(() => () => ref.current?.abort(), []);
  const start = () => {
    const Cls = recognitionClass();
    if (!Cls) return;
    const rec = new Cls();
    rec.lang = "ru-RU";
    rec.interimResults = true;
    rec.continuous = true;
    let finalText = "";
    rec.onresult = (event) => {
      let interim = "";
      finalText = "";
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) finalText += result[0].transcript;
        else interim += result[0].transcript;
      }
      if (!cancelled.current) onText((finalText + interim).trim());
    };
    rec.onerror = (event) => setError(VOICE_ERRORS[event.error] ?? "Голосовой ввод прервался — напишите задачу текстом");
    rec.onend = () => setRecording(false);
    cancelled.current = false;
    setError(null);
    setSeconds(0);
    ref.current = rec;
    rec.start();
    setRecording(true);
  };
  return {
    supported,
    recording,
    seconds,
    error,
    start,
    stop: () => ref.current?.stop(),
    cancel: () => {
      cancelled.current = true;
      ref.current?.abort();
      onText("");
    },
  };
}

// ---- persistence of the current dialog (a per-viewer convenience) ---------------------------------------------------
function storedConversation(userId: string | undefined): string | null {
  try {
    return userId ? localStorage.getItem(`wms-assistant-${userId}`) : null;
  } catch {
    return null;
  }
}

function storeConversation(userId: string | undefined, id: string | null) {
  try {
    if (!userId) return;
    if (id) localStorage.setItem(`wms-assistant-${userId}`, id);
    else localStorage.removeItem(`wms-assistant-${userId}`);
  } catch {
    /* private mode */
  }
}

// ---- cards ---------------------------------------------------------------------------------------------------------
function ProposalCard({ action, onDone }: { action: AssistantAction; onDone: () => void }) {
  const { message } = App.useApp();
  const [key] = useState(requestId);
  const queryClient = useQueryClient();
  const execute = useMutation({
    mutationFn: () => assistant.execute(action.id, key),
    onSuccess: () => {
      onDone();
      queryClient.invalidateQueries({ queryKey: ["expected_receipts"] });
      queryClient.invalidateQueries({ queryKey: ["shipment_orders"] });
    },
    onError: async (e) => {
      // a lost answer: ask the server what happened instead of pressing again
      if (e instanceof ApiError && e.problem.status === 0) {
        message.info("Ответ пока не получен. Проверяем результат; повторно нажимать не нужно.", 6);
        try {
          await assistant.action(action.id);
        } finally {
          onDone();
        }
        return;
      }
      message.error(describeError(e), 8);
      onDone();
    },
  });
  const cancel = useMutation({ mutationFn: () => assistant.cancel(action.id), onSuccess: onDone, onError: (e) => message.error(describeError(e)) });
  const p = action.preview;
  const done = action.status === "succeeded";
  const dead = !action.can_execute && !done;
  return (
    <Card
      size="small"
      className={`wms-assistant-proposal wms-op-${action.intent.startsWith("shipment") || action.intent === "tasks.plan_picking" ? "ship" : "receive"}`}
      title={
        <Space wrap>
          <span>{action.title}</span>
          <Tag color={done ? "green" : dead ? "default" : "blue"}>{action.status_label}</Tag>
          {action.risk === "R2" ? <Tag color="orange">Операция</Tag> : <Tag>Черновик</Tag>}
        </Space>
      }
      data-testid="assistant-proposal"
    >
      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        {p.document ? (
          <Typography.Text>
            Документ: {p.document_path ? <Link to={p.document_path}>{p.document}</Link> : p.document}
            {p.status ? `, статус «${p.status}»` : ""}
            {p.partner && typeof p.partner === "string" ? `, ${p.partner}` : ""}
          </Typography.Text>
        ) : null}
        {p.warehouse ? <Typography.Text type="secondary">Склад: {p.warehouse}</Typography.Text> : null}
        {p.partner && typeof p.partner === "object" ? <Typography.Text>Контрагент: {p.partner.name}</Typography.Text> : null}
        {p.supplier_number ? (
          <Typography.Text>
            Документ контрагента № {p.supplier_number}
            {p.supplier_date ? ` от ${p.supplier_date}` : ""}
          </Typography.Text>
        ) : null}
        {p.lines?.length ? (
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {p.lines.map((line) => (
              <li key={line.line_no}>
                <Typography.Text strong>{line.code}</Typography.Text> {line.name} — {line.text}
                {line.available !== undefined ? <Typography.Text type="secondary"> (свободно {line.available} {line.unit})</Typography.Text> : null}
              </li>
            ))}
          </ol>
        ) : null}
        {p.totals?.length ? <Typography.Text>Итого: {p.totals.map((t) => `${t.quantity} ${t.unit}`).join(" · ")}</Typography.Text> : null}
        {p.warnings?.length ? (
          <Alert
            type="warning"
            showIcon
            message={
              p.warnings.length === 1 ? (
                p.warnings[0]
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {p.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )
            }
          />
        ) : null}
        <Typography.Text type="secondary">{p.effect}</Typography.Text>
        {done && action.result ? (
          <Alert
            type="success"
            showIcon
            message={action.result.text}
            action={action.result.path ? <Link to={action.result.path}>Открыть</Link> : undefined}
          />
        ) : null}
        {action.error && !done ? <Alert type="error" showIcon message={action.error} /> : null}
        {action.can_execute ? (
          <Space wrap>
            <Button type="primary" icon={<CheckCircleOutlined />} loading={execute.isPending} disabled={cancel.isPending} onClick={() => execute.mutate()} data-testid="assistant-execute">
              {action.button}
            </Button>
            <Button icon={<CloseOutlined />} disabled={execute.isPending} loading={cancel.isPending} onClick={() => cancel.mutate()}>
              Отменить
            </Button>
          </Space>
        ) : null}
      </Space>
    </Card>
  );
}

function Bubble({ m, onOption, onReview, refresh, busy }: { m: AssistantMessage; onOption: (text: string) => void; onReview: (id: string) => void; refresh: () => void; busy: boolean }) {
  const mine = m.role === "user";
  if (m.kind === "proposal" && m.action) return <ProposalCard action={m.action} onDone={refresh} />;
  if (m.kind === "error")
    return <Alert type={m.code === "PROVIDER_UNAVAILABLE" ? "warning" : "error"} showIcon icon={<WarningOutlined />} message={m.text} data-testid="assistant-error" />;
  if (m.kind === "result")
    return <Alert type="success" showIcon message={m.text} action={m.path ? <Link to={m.path}>Открыть</Link> : undefined} data-testid="assistant-result" />;
  return (
    <div className={`wms-assistant-bubble ${mine ? "is-mine" : ""}`} data-testid={`assistant-${m.kind}`}>
      {m.kind === "source" ? (
        <Space>
          <FileImageOutlined />
          {m.text}
        </Space>
      ) : (
        <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>
          {m.channel === "voice" && mine ? <AudioOutlined style={{ marginRight: 6 }} /> : null}
          {m.text}
        </Typography.Paragraph>
      )}
      {m.kind === "question" && m.options?.length ? (
        <Space wrap style={{ marginTop: 8 }}>
          {m.options.map((o) => (
            <Button key={o} size="small" disabled={busy} onClick={() => onOption(o)}>
              {o}
            </Button>
          ))}
          <Button size="small" type="text" disabled={busy} onClick={() => onOption("Отмена")}>
            Отмена
          </Button>
        </Space>
      ) : null}
      {m.kind === "extraction" && m.extraction && !m.extraction.error ? (
        <Space wrap style={{ marginTop: 8 }}>
          {m.extraction.document ? (
            <Link to={m.extraction.document.path}>
              <LinkOutlined /> Открыть созданный документ
            </Link>
          ) : null}
          <Button size="small" type="primary" ghost onClick={() => onReview(m.extraction!.id)} data-testid="assistant-open-review">
            {m.extraction.document ? "Показать сверку" : "Проверить и создать"}
          </Button>
        </Space>
      ) : null}
    </div>
  );
}

const SUGGESTIONS = [
  { text: "Сколько на складе наконечников Eppendorf и где они лежат?", need: "" },
  { text: "Что ещё не отгружено сегодня?", need: "" },
  { text: "Создай черновик поступления: ", need: "can_receipt" },
  { text: "Проверь заказ ", need: "" },
];

/**
 * «ИИ Помощник» (ТЗ от 29.10.2026, раздел 13): a task in words or by voice, an invoice photo or PDF, answers from the
 * WMS with the rights of the employee. Every write is a card with its consequences and an explicit button; the
 * server checks rights, warehouse, versions and repeats again when the button is pressed.
 */
export function AssistantPage() {
  const { message } = App.useApp();
  const me = useAuthStore((s) => s.me);
  const userId = me?.user.id;
  const screens = Grid.useBreakpoint();
  const narrow = screens.md === false;
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [channel, setChannel] = useState<"text" | "voice">("text");
  const [conversationId, setConversationId] = useState<string | null>(() => storedConversation(userId));
  const [review, setReview] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  // «Помочь с этим документом»: the card's document is the working context of the next messages
  const [params, setParams] = useSearchParams();
  const docNumber = params.get("document");
  useEffect(() => {
    if (docNumber && !text) setText(`Помоги с документом ${docNumber}: `);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docNumber]);
  const listRef = useRef<HTMLDivElement>(null);

  const caps = useQuery({ queryKey: ["assistant-caps"], queryFn: assistant.capabilities, retry: false });
  const warehouses = useQuery({ queryKey: ["assistant-warehouses"], queryFn: () => api.get<Page<Warehouse>>("/warehouses", { size: 100, sort: "code" }) });
  const conversation = useQuery({
    queryKey: ["assistant-conversation", conversationId],
    queryFn: () => assistant.conversation(conversationId!),
    enabled: !!conversationId,
    retry: false,
  });
  useEffect(() => {
    if (conversation.isError) {
      setConversationId(null);
      storeConversation(userId, null);
    }
  }, [conversation.isError, userId]);

  // the dialogs of this employee on the server: the list behind «История» and the one to go on with when this
  // browser has no pointer (another device, cleared storage) — a dialog is never lost with the browser (section 19)
  const history = useQuery({ queryKey: ["assistant-history"], queryFn: assistant.list, enabled: !!caps.data?.connected });
  const resumed = useRef(false);
  useEffect(() => {
    if (conversationId || resumed.current || !history.data) return;
    resumed.current = true;
    const last = history.data[0];
    if (last) {
      setConversationId(last.id);
      storeConversation(userId, last.id);
    }
  }, [conversationId, history.data, userId]);
  const openDialog = (id: string) => {
    setConversationId(id);
    storeConversation(userId, id);
    setHistoryOpen(false);
  };

  const defaultWarehouse = useMemo(() => {
    const items = warehouses.data?.items ?? [];
    return items.length === 1 ? items[0].id : (items.find((w) => w.code === "MAIN") ?? items[0])?.id ?? null;
  }, [warehouses.data]);
  const current: AssistantConversation | undefined = conversation.data;
  const warehouseId = current?.warehouse?.id ?? null;

  const start = useMutation({
    mutationFn: (wh: string | null) => assistant.start(wh),
    onSuccess: (row) => {
      setConversationId(row.id);
      storeConversation(userId, row.id);
      queryClient.setQueryData(["assistant-conversation", row.id], row);
    },
    onError: (e) => message.error(describeError(e), 6),
  });
  const ensure = async (): Promise<string> => {
    if (conversationId && current) return conversationId;
    const row = await start.mutateAsync(defaultWarehouse);
    return row.id;
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["assistant-conversation", conversationId] });

  const send = useMutation({
    mutationFn: async ({ value, via }: { value: string; via: "text" | "voice" }) => {
      const id = await ensure();
      return { id, result: await assistant.say(id, value, via, docNumber) };
    },
    onMutate: () => {
      setText("");
      setChannel("text");
    },
    onSuccess: ({ id }) => queryClient.invalidateQueries({ queryKey: ["assistant-conversation", id] }),
    onError: (e, vars) => {
      setText(vars.value); // the task is not lost (19)
      message.error(describeError(e), 8);
    },
  });
  const upload = useMutation({
    mutationFn: async (file: File) => uploadInvoice(await ensure(), file),
    onSuccess: (result) => {
      refresh();
      queryClient.invalidateQueries({ queryKey: ["assistant-conversation"] });
      if (result.extraction.status !== "failed") setReview(result.extraction.id);
      if (result.duplicate) message.info("Этот файл уже загружали — показываю прежний результат", 6);
    },
    onError: (e) => message.error(describeError(e), 8),
  });
  const switchWarehouse = useMutation({
    mutationFn: (wh: string) => assistant.setWarehouse(conversationId!, wh),
    onSuccess: (row) => {
      queryClient.setQueryData(["assistant-conversation", row.id], row);
      message.info("Склад диалога изменён: неподтверждённые предложения устарели", 5);
    },
    onError: (e) => message.error(describeError(e)),
  });
  const voice = useVoice((value) => {
    setText(value);
    setChannel(value ? "voice" : "text");
  });

  // a result of a card's button is shown in the card itself; a separate line only for the invoice review
  const carded = new Set((current?.messages ?? []).filter((m) => m.kind === "proposal" && m.action_id).map((m) => m.action_id));
  const messages = (current?.messages ?? []).filter((m) => !(m.kind === "result" && m.action_id && carded.has(m.action_id)));
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, send.isPending, upload.isPending]);

  const submit = (value = text) => {
    const clean = value.trim();
    if (!clean || send.isPending) return;
    if (voice.recording) voice.stop();
    send.mutate({ value: clean, via: channel });
  };

  if (caps.isError) {
    return (
      <>
        <PageHeader title="ИИ Помощник" />
        <Alert type="warning" showIcon message="Помощник недоступен" description={describeError(caps.error)} />
      </>
    );
  }
  const c = caps.data;
  const busy = send.isPending || upload.isPending || start.isPending;

  return (
    <div className="wms-assistant">
      <PageHeader
        title={
          <Space>
            <RobotOutlined />
            ИИ Помощник
          </Space>
        }
        tags={
          <>
            {c ? c.connected ? <Tag color="green">Подключён</Tag> : <Tag color="default">Не подключён</Tag> : null}
            {docNumber ? (
              <Tag
                color="blue"
                closable
                onClose={() => {
                  params.delete("document");
                  setParams(params, { replace: true });
                }}
              >
                Документ {docNumber}
              </Tag>
            ) : null}
          </>
        }
        subtitle={
          <>
            {me?.user.full_name ?? me?.user.username}
            {current?.warehouse ? ` · ${current.warehouse.code} «${current.warehouse.name}»` : ""} — задачи текстом, голосом и по фото накладной. Записывает
            только по вашей кнопке.
          </>
        }
        extra={
          <Space wrap>
            <Select
              style={{ minWidth: 200 }}
              placeholder="Склад"
              value={warehouseId ?? defaultWarehouse ?? undefined}
              disabled={!current || switchWarehouse.isPending}
              onChange={(value) => switchWarehouse.mutate(value)}
              options={(warehouses.data?.items ?? []).map((w) => ({ value: w.id, label: `${w.code} · ${w.name}` }))}
              aria-label="Склад диалога"
            />
            <Button
              icon={<HistoryOutlined />}
              onClick={() => {
                setHistoryOpen(true);
                history.refetch();
              }}
              data-testid="assistant-history"
            >
              История
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => start.mutate(warehouseId ?? defaultWarehouse)}
              loading={start.isPending}
              data-testid="assistant-new"
            >
              Новый диалог
            </Button>
          </Space>
        }
      />
      <Drawer title="История диалогов" open={historyOpen} onClose={() => setHistoryOpen(false)} width={narrow ? "100%" : 440} data-testid="assistant-history-drawer">
        {history.isLoading ? <Spin /> : null}
        {history.data && !history.data.length ? <Empty description="Пока нет диалогов" data-testid="assistant-history-empty" /> : null}
        <List
          dataSource={history.data ?? []}
          renderItem={(row) => (
            <List.Item
              onClick={() => openDialog(row.id)}
              style={{ cursor: "pointer", borderRadius: 8, padding: "10px 12px", background: row.id === conversationId ? "rgba(47, 111, 237, 0.12)" : undefined }}
              data-testid="assistant-history-item"
            >
              <List.Item.Meta
                title={row.title || `Диалог от ${formatDateTime(row.created_at)}`}
                description={[row.warehouse ? `${row.warehouse.code} «${row.warehouse.name}»` : null, row.last_message_at ? `последнее сообщение ${formatDateTime(row.last_message_at)}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              />
              {row.id === conversationId ? <Tag color="blue">текущий</Tag> : null}
            </List.Item>
          )}
        />
      </Drawer>
      {c && !c.connected ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message={c.message} /> : null}
      {c && !c.enabled ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message="ИИ Помощник для веб-интерфейса выключен администратором" /> : null}
      <Card className="wms-assistant-chat" styles={{ body: { padding: 0 } }}>
        <div ref={listRef} className="wms-assistant-list" data-testid="assistant-list">
          {conversation.isLoading ? <Spin style={{ margin: 24 }} /> : null}
          {!messages.length && !conversation.isLoading ? (
            <Empty
              image={<RobotOutlined style={{ fontSize: 44, color: "var(--wms-primary, #2f6fed)" }} />}
              description={
                <Space direction="vertical">
                  <Typography.Text>Напишите задачу или выберите пример. Помощник отвечает по данным WMS и спрашивает, если что-то неясно.</Typography.Text>
                  <div className="wms-assistant-suggestions">
                    {SUGGESTIONS.filter((s) => !s.need || (c && c[s.need as "can_receipt"])).map((s) => (
                      <Button key={s.text} size="small" onClick={() => setText(s.text)}>
                        {s.text}
                      </Button>
                    ))}
                    {c?.documents ? (
                      <Upload accept="image/jpeg,image/png,application/pdf" showUploadList={false} beforeUpload={(file) => (upload.mutate(file), false)}>
                        <Button size="small" icon={<PaperClipOutlined />}>
                          Создать поступление по фото
                        </Button>
                      </Upload>
                    ) : null}
                  </div>
                </Space>
              }
            />
          ) : null}
          {messages.map((m) => (
            <div key={m.id} className={`wms-assistant-row ${m.role === "user" ? "is-mine" : ""}`}>
              <Bubble m={m} busy={busy} onOption={(o) => submit(o)} onReview={setReview} refresh={refresh} />
            </div>
          ))}
          {send.isPending ? (
            <div className="wms-assistant-row">
              <div className="wms-assistant-bubble">
                <Spin size="small" /> <Typography.Text type="secondary">Думаю… смотрю данные склада</Typography.Text>
              </div>
            </div>
          ) : null}
          {upload.isPending ? (
            <div className="wms-assistant-row">
              <div className="wms-assistant-bubble">
                <Spin size="small" /> <Typography.Text type="secondary">Распознаём накладную — обычно 10–30 секунд</Typography.Text>
              </div>
            </div>
          ) : null}
        </div>
        <div className="wms-assistant-input">
          {voice.recording ? (
            <Alert
              type="error"
              showIcon
              icon={<AudioOutlined />}
              message={`Идёт запись ${Math.floor(voice.seconds / 60)}:${String(voice.seconds % 60).padStart(2, "0")} — говорите задачу`}
              action={
                <Space>
                  <Button size="small" icon={<StopOutlined />} onClick={voice.stop}>
                    Остановить
                  </Button>
                  <Button size="small" onClick={voice.cancel}>
                    Отмена
                  </Button>
                </Space>
              }
              style={{ marginBottom: 8 }}
            />
          ) : null}
          {voice.error ? <Alert type="warning" showIcon closable message={voice.error} style={{ marginBottom: 8 }} /> : null}
          <Input.TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            autoSize={{ minRows: 1, maxRows: 6 }}
            placeholder="Напишите задачу: «Создай поступление от Альфа: товар X, 100 шт»"
            disabled={!c?.connected || !c?.enabled}
            maxLength={4000}
            aria-label="Задача для помощника"
            data-testid="assistant-input"
          />
          {channel === "voice" && text ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Расшифровка голоса — проверьте числа и коды перед отправкой.
            </Typography.Text>
          ) : null}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Space wrap>
              <Tooltip title={voice.supported ? "Запись видна, остановить можно в любой момент" : "Этот браузер не распознаёт речь — используйте Chrome, Edge или Safari"}>
                <Button icon={<AudioOutlined />} disabled={!voice.supported || voice.recording || !c?.connected} onClick={voice.start} data-testid="assistant-voice">
                  {narrow ? "Голос" : "Сказать задачу"}
                </Button>
              </Tooltip>
              {c?.documents ? (
                <Upload accept="image/jpeg,image/png,application/pdf" showUploadList={false} disabled={busy || !c?.connected} beforeUpload={(file) => (upload.mutate(file), false)}>
                  <Button icon={<PaperClipOutlined />} disabled={busy || !c?.connected} data-testid="assistant-upload">
                    {narrow ? "Накладная" : "Добавить накладную"}
                  </Button>
                </Upload>
              ) : null}
            </Space>
            <Button type="primary" icon={<SendOutlined />} loading={send.isPending} disabled={!text.trim() || !c?.connected} onClick={() => submit()} data-testid="assistant-send">
              Отправить
            </Button>
          </div>
        </div>
      </Card>
      <Drawer
        open={!!review}
        onClose={() => setReview(null)}
        width={narrow ? "100%" : "min(1280px, 96vw)"}
        title={null}
        closable={false}
        destroyOnHidden
      >
        {review ? <AssistantReview extractionId={review} onClose={() => setReview(null)} onCreated={refresh} /> : null}
      </Drawer>
    </div>
  );
}
